import PropTypes from "prop-types";
import React from "react";
import BottomSheetModal from "./BottomSheetModal";
import {
  SuccessIcon,
  StatusPill,
  SectionRow,
  StepChips,
  Timeline,
  StatsRow,
  Divider,
} from "./SheetPrimitives";

import checkIcon from "../../assets/images/user-dashboard/check.svg";
import flameIcon from "../../assets/images/user-dashboard/flame.svg";
import removeIcon from "../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * GameActivityDetailModal — rich detail sheet for a game/streak-type
 * activity: progress chips, streak history timeline, and summary stats.
 * Opened from a row in the Games table. Built on the shared
 * BottomSheetModal shell.
 * ---------------------------------------------------------------------- */

const formatDateOnly = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const StreakTimeline = ({ items }) => (
  <div className="mc-timeline">
    {items.map((item, index) => (
      <div key={`${item.week}-${index}`} className={`mc-timeline-item mc-timeline-item--${item.tone}`}>
        <span className="mc-timeline-item__dot" />
        <div className="mc-timeline-item__content">
          <div className="mc-timeline-item__left">
            <div className="mc-timeline-item__header">
              <span className="mc-timeline-item__week">
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span>{`Week ${item.week}`}</span>
                  <span className={`mc-timeline-item__status mc-timeline-item__status--${item.tone}`}>
                    {item.status}
                  </span>
                </span>
                {item.weekRange && (
                  <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                    {item.weekRange}
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className="mc-timeline-item__right">
            <span className="mc-timeline-item__points">{item.points}</span>
            {item.date && <span className="mc-timeline-item__date">{formatDateOnly(item.date)}</span>}
          </div>
        </div>
      </div>
    ))}
  </div>
);

StreakTimeline.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      week: PropTypes.number.isRequired,
      weekRange: PropTypes.string,
      status: PropTypes.string.isRequired,
      tone: PropTypes.oneOf(["green", "red", "blue", "grey"]).isRequired,
      points: PropTypes.string.isRequired,
      date: PropTypes.string,
    })
  ).isRequired,
};

const GameActivityDetailModal = ({ game, isOpen, onClose }) => {
  if (!game) return null;

  const { icon, statusLabel, title, description, progressLabel, progressValue, weeks, history, stats, footnote, gameType, progressPercent } = game;
  const isVisitType = gameType === 'visits';
  const isStreakType = gameType === 'streak';

  return (
    <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="gadm-title">
      <div className="mc-step-header">
        <p className="mc-eyebrow">Game Activity Detail</p>
        <SuccessIcon icon={icon} tone="orange" />
        <StatusPill label={statusLabel} />
      </div>
      <h2 className="mc-title mc-title--center" id="gadm-title">
        {title}
      </h2>
      <p className="mc-description mc-description--center">{description}</p>

      <SectionRow label={progressLabel} meta={progressValue} />
      {isVisitType ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            backgroundColor: '#e5e7eb', 
            borderRadius: '4px', 
            overflow: 'hidden' 
          }}>
            <div style={{ 
              width: `${progressPercent || 0}%`, 
              height: '100%', 
              backgroundColor: '#ec421c', 
              borderRadius: '4px',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', textAlign: 'center' }}>
            {progressPercent || 0}% Complete
          </p>
        </div>
      ) : isStreakType ? (
        <StepChips steps={weeks} doneIcon={checkIcon} pendingIcon={flameIcon} failedIcon={removeIcon} />
      ) : null}

      {(isVisitType || isStreakType) && (
        <>
          <p className="mc-section-label">{isVisitType ? "Visit History" : "Streak History"}</p>
          {isVisitType ? (
            <Timeline items={history} isVisitType={true} />
          ) : (
            <StreakTimeline items={history} />
          )}
        </>
      )}

      <Divider />
      <StatsRow stats={stats} />
      <Divider />

      <p className="mc-footnote">{footnote}</p>
    </BottomSheetModal>
  );
};

GameActivityDetailModal.propTypes = {
  game: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    statusLabel: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    progressLabel: PropTypes.string.isRequired,
    progressValue: PropTypes.string.isRequired,
    weeks: PropTypes.array.isRequired,
    history: PropTypes.array.isRequired,
    stats: PropTypes.array.isRequired,
    footnote: PropTypes.string.isRequired,
    gameType: PropTypes.string,
    progressPercent: PropTypes.number,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

GameActivityDetailModal.defaultProps = { game: null };

export default GameActivityDetailModal;
